import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { buildBreadcrumb } from "@/lib/seo";
import { Smartphone, Apple, Share, Plus, MoreVertical, Download as DownloadIcon, QrCode, ShieldCheck, Zap, Bell } from "lucide-react";

export default function Download() {
  const [deferred, setDeferred] = useState(null);
  const url = typeof window !== "undefined" ? window.location.origin : "https://adn-marketplace.com";

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerInstall = () => {
    if (deferred) deferred.prompt();
    else alert("Open this page on your phone in Chrome (Android) or Safari (iPhone) to install.");
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&bgcolor=FAFAFA&color=0B132B&qzone=1&data=${encodeURIComponent(url)}`;

  return (
    <div>
      <Seo
        title="Download the ADN App"
        description="Install ADN Marketplace on iPhone, Android, or desktop. One tap from your home screen — no app store, no download wait."
        path="/download"
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", url: "https://adn-marketplace.com/" },
            { name: "Download", url: "https://adn-marketplace.com/download" },
          ]),
        ]}
      />
      <Navbar />

      {/* HERO */}
      <section className="bg-[var(--adn-navy)] text-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow text-[var(--adn-gold)]">Get the app</p>
            <h1 className="font-display text-5xl sm:text-6xl font-light mt-3 leading-[1.05] tracking-tight">
              ADN in your pocket.<br />
              <span className="text-[var(--adn-gold)] italic font-light">One tap away.</span>
            </h1>
            <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-xl">
              Install ADN Marketplace as an app on your phone or desktop. No App Store download, no Play Store wait — works on iPhone, Android, Mac, and Windows.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button data-testid="dl-install-now" onClick={triggerInstall} className="btn-gold px-7 py-4 rounded inline-flex items-center justify-center gap-2 text-[15px]">
                <DownloadIcon size={18} /> Install Now
              </button>
              <a href="#instructions" className="btn-outline-light px-7 py-4 rounded inline-flex items-center justify-center text-[15px]">
                Step-by-step
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md text-xs uppercase tracking-[0.18em] text-white/65">
              <span className="flex items-center gap-2"><Zap size={14} className="text-[var(--adn-gold)]" /> Instant</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-[var(--adn-emerald)]" /> Secure</span>
              <span className="flex items-center gap-2"><Bell size={14} className="text-[var(--adn-gold)]" /> Alerts</span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center">
            <div className="bg-white p-5 rounded-md shadow-2xl">
              <img src={qrSrc} alt="Scan to open ADN on your phone" className="w-[220px] h-[220px]" />
              <p className="text-center text-[var(--adn-navy)] font-display font-semibold mt-3 text-sm">Scan with your phone</p>
              <p className="text-center text-[var(--adn-text-muted)] text-xs mt-1">Then add to home screen</p>
            </div>
          </div>
        </div>
      </section>

      {/* INSTRUCTIONS */}
      <section id="instructions" className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <p className="eyebrow text-center">How to install</p>
        <h2 className="font-display text-4xl sm:text-5xl font-medium mt-3 text-center max-w-2xl mx-auto">Pick your device.</h2>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {/* iOS */}
          <div className="bg-white border border-[var(--adn-line)] p-7">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-black flex items-center justify-center text-white">
                <Apple size={20} />
              </div>
              <div>
                <p className="font-display text-lg font-semibold">iPhone & iPad</p>
                <p className="text-xs text-[var(--adn-text-muted)]">Safari only</p>
              </div>
            </div>
            <ol className="mt-6 space-y-4 text-sm">
              <Step n={1} text={<>Open <span className="font-semibold">adn-marketplace.com</span> in Safari (not Chrome).</>} />
              <Step n={2} text={<>Tap the <Share size={14} className="inline mx-0.5 text-[var(--adn-gold)]" /> <span className="font-semibold">Share</span> icon at the bottom of the screen.</>} />
              <Step n={3} text={<>Scroll down and tap <span className="font-semibold">Add to Home Screen <Plus size={14} className="inline" /></span>.</>} />
              <Step n={4} text={<>Tap <span className="font-semibold">Add</span> in the top-right.</>} />
              <Step n={5} text={<>Open ADN from your home screen — it launches full-screen like a real app.</>} />
            </ol>
          </div>

          {/* Android */}
          <div className="bg-white border border-[var(--adn-line)] p-7">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[var(--adn-emerald)] flex items-center justify-center text-white">
                <Smartphone size={20} />
              </div>
              <div>
                <p className="font-display text-lg font-semibold">Android</p>
                <p className="text-xs text-[var(--adn-text-muted)]">Chrome / Edge / Brave</p>
              </div>
            </div>
            <ol className="mt-6 space-y-4 text-sm">
              <Step n={1} text={<>Open <span className="font-semibold">adn-marketplace.com</span> in Chrome.</>} />
              <Step n={2} text={<>Tap the <MoreVertical size={14} className="inline text-[var(--adn-gold)]" /> menu in the top-right corner.</>} />
              <Step n={3} text={<>Tap <span className="font-semibold">Install app</span> (or "Add to Home screen").</>} />
              <Step n={4} text={<>Confirm by tapping <span className="font-semibold">Install</span>.</>} />
              <Step n={5} text={<>ADN appears in your app drawer with its own icon — open it from there or your home screen.</>} />
            </ol>
            <button data-testid="dl-android-install" onClick={triggerInstall} className="mt-6 w-full btn-outline-navy px-4 py-2.5 rounded text-sm">
              Or tap here on Android
            </button>
          </div>

          {/* Desktop */}
          <div className="bg-white border border-[var(--adn-line)] p-7">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[var(--adn-gold)] flex items-center justify-center text-[var(--adn-navy)]">
                <DownloadIcon size={20} />
              </div>
              <div>
                <p className="font-display text-lg font-semibold">Mac & Windows</p>
                <p className="text-xs text-[var(--adn-text-muted)]">Chrome / Edge</p>
              </div>
            </div>
            <ol className="mt-6 space-y-4 text-sm">
              <Step n={1} text={<>Open <span className="font-semibold">adn-marketplace.com</span> in Chrome or Edge.</>} />
              <Step n={2} text={<>Look for the <DownloadIcon size={14} className="inline text-[var(--adn-gold)]" /> install icon in the address bar (right side).</>} />
              <Step n={3} text={<>Click it and confirm <span className="font-semibold">Install</span>.</>} />
              <Step n={4} text={<>ADN opens in its own window like a native desktop app.</>} />
            </ol>
            <button data-testid="dl-desktop-install" onClick={triggerInstall} className="mt-6 w-full btn-outline-navy px-4 py-2.5 rounded text-sm">
              Install on this computer
            </button>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white border-y border-[var(--adn-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <p className="eyebrow text-center">Why install</p>
          <h2 className="font-display text-4xl font-medium mt-3 text-center">Native-feel. Zero install wait.</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {[
              { i: Zap, t: "Instant launch", d: "Opens straight from your home screen — no browser tabs." },
              { i: Bell, t: "Stay updated", d: "Get the latest version automatically every time you open it." },
              { i: ShieldCheck, t: "Same security", d: "Encrypted just like the website. No extra permissions." },
              { i: Smartphone, t: "Full-screen UI", d: "No browser address bar getting in the way." },
            ].map((f, i) => (
              <div key={i}>
                <f.i size={22} className="text-[var(--adn-gold)]" />
                <h3 className="font-display text-lg font-semibold mt-3">{f.t}</h3>
                <p className="text-sm text-[var(--adn-text-soft)] mt-1 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20">
        <p className="eyebrow">FAQ</p>
        <h2 className="font-display text-4xl font-medium mt-3 mb-10">Common questions.</h2>
        <div className="space-y-7">
          {[
            { q: "Is this a real app from the App Store or Play Store?", a: "ADN Marketplace is a Progressive Web App (PWA) — it installs directly from your browser, looks and behaves like a native app, but does not require an App Store or Play Store download. You always get the latest version and never need to update manually." },
            { q: "Will it use a lot of storage?", a: "Less than 10 MB on most devices — that is roughly the size of a single photo." },
            { q: "Can I use it offline?", a: "The app shell loads offline, so you can open ADN even without a connection. New offers, messages, and data require an active internet connection." },
            { q: "Do I need to create a new account?", a: "No. Use the same email/Google login as the website. Everything syncs." },
            { q: "How do I uninstall?", a: "Just long-press the icon and remove it like any other app. On desktop, click the three-dot menu inside the installed window and choose Uninstall." },
          ].map((f, i) => (
            <details key={i} className="border-b border-[var(--adn-line)] pb-5 group">
              <summary className="font-display text-lg font-semibold cursor-pointer list-none flex items-start justify-between gap-4">
                <span>{f.q}</span>
                <span className="text-[var(--adn-gold)] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-[var(--adn-text-soft)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Step({ n, text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="h-6 w-6 rounded-full bg-[var(--adn-gold)] text-[var(--adn-navy)] font-display font-bold text-xs flex items-center justify-center shrink-0">{n}</span>
      <span className="text-[var(--adn-text-soft)] leading-relaxed pt-0.5">{text}</span>
    </li>
  );
}
